import React, { createContext, useState, useContext, useEffect } from 'react';
import { message } from 'antd';
import { createNewSession, sendMessage } from '../services/QianfangChat';

export const QianfangChatContext = createContext();

export const QianfangChatProvider = ({ children }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);

  // 加载历史会话
  useEffect(() => {
    const savedSessions = localStorage.getItem('qianfang_chat_sessions');
    if (savedSessions) {
      setSessions(JSON.parse(savedSessions));
    }
  }, []);

  // 保存会话到本地
  useEffect(() => {
    if (sessions.length > 0) {
      localStorage.setItem('qianfang_chat_sessions', JSON.stringify(sessions));
    }
  }, [sessions]);

  // 创建新会话
  const createSession = async () => {
    setLoading(true);
    try {
      // 先添加初始欢迎消息，不依赖于API响应
      setMessages([{
        role: 'assistant',
        content: '您好，我是千方智能助手。我可以回答您的各类问题，请问有什么可以帮助您的？',
        timestamp: new Date().toISOString()
      }]);
      
      // 创建一个临时会话ID
      const tempSessionId = `session_${Date.now()}`;
      
      // 创建新会话对象
      const newSession = {
        id: tempSessionId,
        name: `对话 ${new Date().toLocaleString()}`,
        createdAt: new Date().toISOString(),
      };
      
      setSessions(prev => [newSession, ...prev]);
      setCurrentSessionId(tempSessionId);
      
      // 尝试从API获取真实会话ID
      try {
        const sessionId = await createNewSession();
        if (sessionId) {
          // 更新会话ID
          setCurrentSessionId(sessionId);
          // 更新会话列表
          setSessions(prev => prev.map(session => 
            session.id === tempSessionId ? { ...session, id: sessionId } : session
          ));
        }
      } catch (error) {
        console.error('API创建会话失败，使用临时会话ID:', error);
        // 使用临时会话ID继续，不影响用户体验
      }
      
      return tempSessionId;
    } catch (error) {
      console.error('创建会话完全失败:', error);
      message.error('创建会话失败，请刷新页面重试');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // 发送消息
  const sendChatMessage = async (content) => {
    setLoading(true);
    setError(null);
    
    // 添加用户消息到消息列表
    const userMessage = { 
      role: 'user', 
      content, 
      timestamp: new Date().toISOString() 
    };
    setMessages(prev => [...prev, userMessage]);
    
    // 添加临时的"思考中"消息
    const tempMessage = {
      role: 'assistant',
      content: '思考中...',
      timestamp: new Date().toISOString(),
      temporary: true
    };
    setMessages(prev => [...prev, tempMessage]);
    
    try {
      console.log('QianfangChatContext: 发送消息:', content);
      console.log('QianfangChatContext: 当前会话ID:', currentSessionId);
      
      const response = await sendMessage(content, currentSessionId);
      
      console.log('QianfangChatContext: 收到响应:', response);
      
      // 移除临时消息
      setMessages(prev => prev.filter(msg => !msg.temporary));
      
      // 添加AI回复
      const aiMessage = {
        role: 'assistant',
        content: response.output.text,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // 更新会话ID
      if (response.output.session_id) {
        setCurrentSessionId(response.output.session_id);
      }
      
      return response;
    } catch (error) {
      console.error('QianfangChatContext: 发送消息错误:', error);
      
      // 移除临时消息
      setMessages(prev => prev.filter(msg => !msg.temporary));
      
      // 添加错误消息
      const errorMessage = {
        role: 'assistant',
        content: `发送消息失败: ${error.message || '未知错误'}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <QianfangChatContext.Provider value={{
      sessions,
      currentSessionId,
      setCurrentSessionId,
      messages,
      setMessages,
      loading,
      createSession,
      sendChatMessage,
      error
    }}>
      {children}
    </QianfangChatContext.Provider>
  );
};

export const useQianfangChat = () => useContext(QianfangChatContext); 